
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { 
  Clock, 
  CreditCard, 
  PenTool, 
  Package, 
  Truck, 
  Home,
  ChevronRight,
  Trash2,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useLocalization } from '../providers/LocalizationProvider'; // Added useLocalization import
import { determineDeliveryStatus } from './utils';

const statusConfig = {
  ordered: { 
    icon: Clock, 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-50 border-blue-200 text-blue-800',
    titleKey: 'order_statuses.ordered'
  },
  paid: { 
    icon: CreditCard, 
    color: 'bg-green-500', 
    lightColor: 'bg-green-50 border-green-200 text-green-800',
    titleKey: 'order_statuses.paid'
  },
  signed: { 
    icon: PenTool, 
    color: 'bg-purple-500', 
    lightColor: 'bg-purple-50 border-purple-200 text-purple-800',
    titleKey: 'order_statuses.signed'
  },
  packed: { 
    icon: Package, 
    color: 'bg-orange-500', 
    lightColor: 'bg-orange-50 border-orange-200 text-orange-800',
    titleKey: 'order_statuses.packed'
  },
  shipped: { 
    icon: Truck, 
    color: 'bg-indigo-500', 
    lightColor: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    titleKey: 'order_statuses.shipped'
  },
  delivered: { 
    icon: Home, 
    color: 'bg-emerald-500', 
    lightColor: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    titleKey: 'order_statuses.delivered'
  }
};

// New OrderCard component extracted from the pipeline
const OrderCard = ({ order, onOrderSelect, onOrderDelete, onOrderRestore, showDeleted }) => {
  const { currencySymbol, t } = useLocalization(); // Use localization hook to get currency symbol and translations
  const isDeleted = order.is_deleted;
  
  const deliveryStatus = useMemo(() => {
    return determineDeliveryStatus(order);
  }, [order.shipment_details, order.status]);
  return (
    <div
      className={`p-2 rounded-lg border transition-all duration-200 group ${
        isDeleted
          ? 'border-red-200 bg-red-50/50 opacity-75' 
          : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 cursor-pointer'
      }`}
    >
      <div className="flex justify-between items-start mb-2">
        <p className={`font-semibold text-sm transition-colors ${
          isDeleted 
            ? 'text-red-600' 
            : 'text-slate-900 group-hover:text-amber-700'
        }`} onClick={(e) => {
          e.stopPropagation(); // Stop propagation to prevent Draggable from getting the click and breaking selection
          onOrderSelect(order);
        }}>
          #{order.order_number || order.id.slice(-6)}
          {isDeleted && <span className="text-xs ml-1">(Удален)</span>}
        </p>
        <div className="flex items-center gap-1">
          {showDeleted && isDeleted ? (
            <Button
              variant="ghost"
              size="icon"
              className="w-6 h-6 text-green-600 hover:text-green-800"
              onClick={(e) => {
                e.stopPropagation();
                onOrderRestore(order.id);
              }}
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          ) : !isDeleted ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="w-6 h-6 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Удалить заказ?')) {
                    onOrderDelete(order.id);
                  }
                }}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
              {/* This div's onClick is from the outline, handling ChevronRight click for select */}
              <div onClick={(e) => {
                e.stopPropagation(); // Stop propagation to prevent Draggable from getting the click and breaking selection
                onOrderSelect(order);
              }}>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-amber-500 transition-colors" />
              </div>
            </>
          ) : null}
        </div>
      </div>
      
      {/* This div handles the onOrderSelect click for the main card content, preventing it if the order is deleted */}
      <div onClick={() => !isDeleted && onOrderSelect(order)}> 
        <p className="text-sm font-medium text-slate-700 mb-1">
          {order.customer_name}
        </p>
        
        <p className="text-xs text-slate-500 mb-2">
          {format(new Date(order.created_date), 'MMM d, h:mm a')}
        </p>
        
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold text-slate-900">
                            {currencySymbol}{Number(order.total_amount || 0).toFixed(2)}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">
              {order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0)} items
            </span>
            {deliveryStatus && (
              <div 
                className={`${deliveryStatus.color} cursor-help`}
                title={t(`delivery_status.tooltip.${deliveryStatus.status}`)}
              >
                <deliveryStatus.icon className="w-4 h-4" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


export default function OrderPipeline({ orders, onOrderSelect, onOrderStatusDrop, onOrderDelete, onOrderRestore, showDeleted, isLoading }) { // Added onOrderStatusDrop prop
  const { t } = useLocalization();
  const statuses = Object.keys(statusConfig);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    // If no destination or dropped back into the same place, do nothing
    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    
    // Call handler from parent to update status
    // draggableId is the order ID, destination.droppableId is the new status
    onOrderStatusDrop(draggableId, destination.droppableId);
  };


  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statuses.map((status) => (
          <Card key={status} className="elegant-card">
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-4 w-8" />
            </CardHeader>
            <CardContent className="space-y-3">
              {Array(3).fill(0).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // The `orders` prop is now expected to be pre-filtered based on `showDeleted` by the parent component.
  // The `is_deleted` check for `isDragDisabled` and display still applies within the `OrderCard`.
  const ordersByStatus = statuses.reduce((acc, status) => {
    acc[status] = orders.filter(order => order.status === status);
    return acc;
  }, {});

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid gap-3 items-start grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-7"> {/* auto-fill style grid for better width usage */}
        {statuses.map((status) => {
          const config = statusConfig[status];
          const Icon = config.icon;
          const statusOrders = ordersByStatus[status] || []; // Ensure it's an array

          return (
            <Droppable key={status} droppableId={status}>
              {(provided, snapshot) => (
                <Card 
                  ref={provided.innerRef} // Apply ref from Droppable
                  {...provided.droppableProps} // Apply droppable props
                  className={`elegant-card hover:shadow-lg transition-all duration-300 ${snapshot.isDraggingOver ? 'bg-amber-50/50' : ''}`} // Add styling for dragging over
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center justify-between text-base">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-lg ${config.color} bg-opacity-20`}>
                          <Icon className={`w-4 h-4 ${config.color.replace('bg-', 'text-')}`} />
                        </div>
                        <span className="font-bold text-slate-900">{t(config.titleKey)}</span>
                      </div>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600 font-semibold">
                        {statusOrders.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="p-2 space-y-3 min-h-[120px]"> {/* min-h added for droppable area; height no longer capped */}
                    {statusOrders.length === 0 ? (
                      // Only show "No orders" if not currently dragging something over this column
                      !snapshot.isDraggingOver && ( 
                        <div className="text-center py-8 text-slate-400">
                          <Icon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm font-medium">Нет заказов</p>
                        </div>
                      )
                    ) : (
                      statusOrders.map((order, index) => (
                        <Draggable 
                          key={order.id} 
                          draggableId={order.id} 
                          index={index} 
                          isDragDisabled={order.is_deleted} // Prevent dragging deleted orders
                        >
                          {(provided, snapshot) => (
                             <div
                              ref={provided.innerRef} // Apply ref from Draggable
                              {...provided.draggableProps} // Apply draggable props
                              {...provided.dragHandleProps} // Apply drag handle props (allows dragging the whole element)
                              style={{
                                ...provided.draggableProps.style,
                                boxShadow: snapshot.isDragging ? '0 4px 8px rgba(0,0,0,0.1)' : 'none', // Add shadow when dragging
                              }}
                            >
                              <OrderCard 
                                order={order}
                                onOrderSelect={onOrderSelect}
                                onOrderDelete={onOrderDelete}
                                onOrderRestore={onOrderRestore}
                                showDeleted={showDeleted}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder} {/* Placeholder for items being dragged */}
                  </CardContent>
                </Card>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
}
